import compact from 'lodash/compact';

import {
   MedicalDeviceAwardCriteria,
   MedicalDeviceFieldsConfiguration,
   MedicalDeviceOrder,
   MedicalDeviceOrderDetails,
   MedicalDevicePlacement,
} from '@dreryk/egabinet-ezwm-frontend-api';

import { Slot } from '@dreryk/watermelon';
import { BindingCreator } from '@dreryk/react-common';

import {
   MedicalDeviceOrderAdditionalIndicationsFormBeanPart,
   MedicalDeviceOrderAllFieldsFormBean,
   MedicalDeviceOrderContinuationFormBeanPart,
   MedicalDeviceOrderDeviceAwardCriteriaFormBeanPart,
   MedicalDeviceOrderFirstSupplyMonthFormBeanPart,
   MedicalDeviceOrderFistulaCountFormBeanPart,
   MedicalDeviceOrderFistulaTypeFormBeanPart,
   MedicalDeviceOrderFormBean,
   MedicalDeviceOrderFormRegister,
   MedicalDeviceOrderFormWatch,
   MedicalDeviceOrderItemsCountFormBeanPart,
   MedicalDeviceOrderItemsPerMonthCountFormBeanPart,
   MedicalDeviceOrderPlacementFormBeanPart,
   MedicalDeviceOrderPregnantFormBeanPart,
   MedicalDeviceOrderRefundLimitMultiplicityFormBeanPart,
   MedicalDeviceOrderSupplyMonthsCountFormBeanPart,
   MedicalDeviceOrderTillLimitFormBeanPart,
} from './medicalDeviceOrder.types';

export const shouldDisplayPregnantField = (
   register: MedicalDeviceOrderFormRegister,
   pregnantFieldAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderPregnantFormBeanPart> => {
   return pregnantFieldAvailable;
};

export const shouldDisplayContinuationField = (
   register: MedicalDeviceOrderFormRegister,
   continuationAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderContinuationFormBeanPart> =>
   continuationAvailable;

export const shouldDisplayPlacementField = (
   register: MedicalDeviceOrderFormRegister,
   placementAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderPlacementFormBeanPart> =>
   placementAvailable;

export const shouldDisplayItemsCountReadOnlyField = (
   register: MedicalDeviceOrderFormRegister,
   itemsCountAvailable: boolean,
   limit: number
): register is BindingCreator<
   MedicalDeviceOrderFormBean & MedicalDeviceOrderItemsCountFormBeanPart & MedicalDeviceOrderPlacementFormBeanPart
> => itemsCountAvailable && limit === 1;

export const shouldDisplayItemsCountField = (
   register: MedicalDeviceOrderFormRegister,
   itemsCountAvailable: boolean,
   limit: number
): register is BindingCreator<
   MedicalDeviceOrderFormBean & MedicalDeviceOrderItemsCountFormBeanPart & MedicalDeviceOrderPlacementFormBeanPart
> => itemsCountAvailable && limit > 1;

export const shouldDisplayItemsPerMonthCountField = (
   register: MedicalDeviceOrderFormRegister,
   itemsCountPerMonthAvailable: boolean
): register is BindingCreator<
   MedicalDeviceOrderFormBean &
      MedicalDeviceOrderItemsPerMonthCountFormBeanPart &
      MedicalDeviceOrderTillLimitFormBeanPart
> => itemsCountPerMonthAvailable;

export const shouldDisplayRefundLimitMultiplicityField = (
   register: MedicalDeviceOrderFormRegister,
   refundLimitMultiplicityAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderRefundLimitMultiplicityFormBeanPart> =>
   refundLimitMultiplicityAvailable;

export const shouldDisplayFistulaCountField = (
   register: MedicalDeviceOrderFormRegister,
   fistulaCountAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderFistulaCountFormBeanPart> =>
   fistulaCountAvailable;

export const shouldDisplayFirstSupplyMonthField = (
   register: MedicalDeviceOrderFormRegister,
   firstSupplyMonthAvailable: boolean
): register is BindingCreator<
   MedicalDeviceOrderFormBean &
      MedicalDeviceOrderFirstSupplyMonthFormBeanPart &
      MedicalDeviceOrderContinuationFormBeanPart
> => firstSupplyMonthAvailable;

export const shouldDisplaySupplyMonthsCountField = (
   register: MedicalDeviceOrderFormRegister,
   supplyMonthsCountAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderSupplyMonthsCountFormBeanPart> =>
   supplyMonthsCountAvailable;

export const shouldDisplayTillLimitField = (
   register: MedicalDeviceOrderFormRegister,
   limitPerMonth: number
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderTillLimitFormBeanPart> =>
   limitPerMonth > 1;

export const shouldDisplayAdditionalIndicationsSection = (
   register: MedicalDeviceOrderFormRegister,
   additionalIndicationsAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderAdditionalIndicationsFormBeanPart> =>
   additionalIndicationsAvailable;

export const shouldDisplayDeviceAwardCriteriaTable = (
   register: MedicalDeviceOrderFormRegister,
   medicalDeviceFetched: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderDeviceAwardCriteriaFormBeanPart> =>
   medicalDeviceFetched;

export const shouldDisplayFistulaTypeField = (
   register: MedicalDeviceOrderFormRegister,
   fistulaTypeAvailable: boolean
): register is BindingCreator<MedicalDeviceOrderFormBean & MedicalDeviceOrderFistulaTypeFormBeanPart> =>
   fistulaTypeAvailable;

export const isMedicalDeviceFieldAvailable = (
   watch: MedicalDeviceOrderFormWatch,
   field: keyof MedicalDeviceFieldsConfiguration
): boolean => Boolean(watch('device')?.fieldsConfiguration[field].available);

export const isMedicalDeviceFieldRequired = (
   watch: MedicalDeviceOrderFormWatch,
   fieldName: keyof MedicalDeviceFieldsConfiguration
): boolean => {
   const device = watch('device');
   const field = device?.fieldsConfiguration[fieldName];
   return field?.required ?? false;
};

const calculatePlacement = (
   medicalDeviceDraft: MedicalDeviceOrder
): MedicalDeviceOrderPlacementFormBeanPart['placement'] | undefined => {
   // eslint-disable-next-line no-undef-init
   let placement: MedicalDeviceOrderPlacementFormBeanPart['placement'] | undefined = undefined;
   if (medicalDeviceDraft.placement) {
      placement = medicalDeviceDraft.placement === 'BOTH' ? ['LEFT', 'RIGHT'] : [medicalDeviceDraft.placement];
   }
   return placement;
};

export const mapMedicalDeviceDraftToFormValues = (
   medicalDeviceDraft: MedicalDeviceOrder
): Partial<MedicalDeviceOrderAllFieldsFormBean> => {
   const deviceAwardCriteria: MedicalDeviceAwardCriteria[] = compact(
      medicalDeviceDraft.awardCriteriaIds.map((criteriaId) =>
         medicalDeviceDraft.medicalDevice?.awardCriteria.find((criteria) => criteria.id === criteriaId)
      )
   );

   return {
      pregnant: medicalDeviceDraft.pregnant ?? false,
      tillLimit: false,
      firstSupplyMonth: medicalDeviceDraft.firstSupplyDate ? new Date(medicalDeviceDraft.firstSupplyDate) : undefined,
      supplyMonthsCount: medicalDeviceDraft.supplyMonthsCount ?? undefined,
      itemsCount: medicalDeviceDraft.itemsCount ?? undefined,
      itemsPerMonthCount: medicalDeviceDraft.itemsPerMonthCount ?? undefined,
      device: medicalDeviceDraft.medicalDevice ?? undefined,
      deviceType: medicalDeviceDraft.medicalDevice?.medicalDeviceType ?? 'OTHER',
      fistulaCount: medicalDeviceDraft.fistulaCount ?? undefined,
      additionalIndications: medicalDeviceDraft.shorteningDeviceUseTimeSpan ? ['shorteningDeviceUseTimeSpan'] : [],
      shorteningDeviceUseTimeSpanJustification: medicalDeviceDraft.shorteningDeviceUseTimeSpanReasonDescription ?? '',
      shorteningDeviceUseTimeSpanReason: medicalDeviceDraft.shorteningDeviceUseTimeSpanReasonForChildren ?? undefined,
      disease:
         medicalDeviceDraft.diseaseIcd10Code && medicalDeviceDraft.diseaseIcd10Name
            ? {
                 id: 1, // Mock value, not being sent to backend. It is just to satisfy TS
                 icd10Code: medicalDeviceDraft.diseaseIcd10Code,
                 name: medicalDeviceDraft.diseaseIcd10Name,
              }
            : undefined,
      // TODO DREGAB-4872 To be done as part of whole epic
      additionalEntitlementCode: '47IB',
      placement: calculatePlacement(medicalDeviceDraft),
      deviceAwardCriteria: deviceAwardCriteria,
      allDeviceAwardCriteria:
         medicalDeviceDraft.awardCriteriaIds?.length === medicalDeviceDraft.medicalDevice?.awardCriteria.length,
      continuation: medicalDeviceDraft.continuation ?? undefined,
      fistulaType: medicalDeviceDraft.fistulaType ?? undefined,
      refundLimitMultiplicity: medicalDeviceDraft.refundLimitMultiplicity ?? undefined,
      additionalInformation: medicalDeviceDraft.additionalInformation ?? undefined,
   };
};

export const mapFormBeanToMedicalDeviceOrderDetails = (
   formValues: MedicalDeviceOrderAllFieldsFormBean
): MedicalDeviceOrderDetails => {
   const { placement, device } = formValues;
   let payloadPlacement = null;
   if (placement) {
      payloadPlacement = (placement.length === 1 ? placement.at(0) ?? null : 'BOTH') as MedicalDevicePlacement | null;
   }
   const shouldApplyShorteningDeviceUseTimeSpan = Boolean(device?.fieldsConfiguration.additionalIndications.available);
   const shouldApplyContinuation = Boolean(device?.fieldsConfiguration.continuation.available);
   return {
      medicalDeviceId: formValues.device!.id,
      continuation: Slot.emptyIfNull(shouldApplyContinuation ? Boolean(formValues.continuation) : null),
      itemsCount: Slot.emptyIfNull(formValues.itemsCount ?? null),
      awardCriteriaIds: formValues.deviceAwardCriteria!.map(({ id }) => id),
      diseaseIcd10Code: formValues.disease!.icd10Code,
      diseaseIcd10Name: formValues.disease!.name,
      firstSupplyDate: Slot.emptyIfNull(formValues.firstSupplyMonth ?? null),
      fistulaCount: Slot.emptyIfNull(formValues.fistulaCount ?? null),
      itemsPerMonthCount: Slot.emptyIfNull(formValues.itemsPerMonthCount ?? null),
      placement: Slot.emptyIfNull(payloadPlacement ?? null),
      pregnant: Slot.emptyIfNull(formValues.pregnant ?? null),
      shorteningDeviceUseTimeSpan: Slot.emptyIfNull(
         shouldApplyShorteningDeviceUseTimeSpan
            ? formValues.additionalIndications?.at(0) === 'shorteningDeviceUseTimeSpan'
            : null
      ),
      shorteningDeviceUseTimeSpanReasonDescription: Slot.emptyIfNull(
         !formValues.shorteningDeviceUseTimeSpanJustification ||
            formValues.shorteningDeviceUseTimeSpanJustification.length === 0
            ? null
            : formValues.shorteningDeviceUseTimeSpanJustification
      ),
      useTimeSpanShorteningForChildrenReason: Slot.emptyIfNull(formValues.shorteningDeviceUseTimeSpanReason ?? null),
      supplyMonthsCount: Slot.emptyIfNull(formValues.supplyMonthsCount ?? null),
      fistulaType: Slot.emptyIfNull(formValues.fistulaType ?? null),
      refundLimitMultiplicity: Slot.emptyIfNull(formValues.refundLimitMultiplicity ?? null),
      additionalInformation: Slot.emptyIfNull(formValues.additionalInformation ?? null),
   };
};
